import { AllotmentDirection, FareType, MarginType, MarginValueType, } from '@massilia/prisma-generated/enums';
import { z } from 'zod';
import { isoDateString } from '../_shared/dates.js';
const marginPeriodSchema = z.object({
    endDate: isoDateString(),
    startDate: isoDateString(),
});
export const allotmentDailyMarginSchema = z.object({
    date: z.string(),
    direction: z.nativeEnum(AllotmentDirection).optional(),
    fixedMarginAmount: z.number().min(0),
    isDisabled: z.boolean().optional(),
});
export const allotmentMonthConfigSchema = z.object({
    direction: z.nativeEnum(AllotmentDirection).optional(),
    isDisabled: z.boolean(),
    month: z.number().min(1).max(12),
    year: z.number(),
});
export const marginPriceRangeSchema = z.object({
    maxPrice: z.number().min(0).nullable(),
    minPrice: z.number().min(0),
    value: z.number().positive(),
    valueType: z.nativeEnum(MarginValueType),
});
export const routeSegmentSchema = z.object({
    destinationIataCodes: z.array(z.string()),
    originIataCodes: z.array(z.string()),
});
// Fields whose definition is identical in create + update (all optional).
const marginCommonFields = {
    accountCode: z.string().optional(),
    companyFixedMargin: z.number().positive().optional(),
    dailyMargins: z.array(allotmentDailyMarginSchema).optional(),
    destinationIataCode: z.string().optional(),
    destinationIataCodes: z.array(z.string()).optional(),
    fareType: z.nativeEnum(FareType).optional(),
    fixedMarginAmount: z.number().positive().optional(),
    isActive: z.boolean().optional(),
    monthConfigs: z.array(allotmentMonthConfigSchema).optional(),
    negotiatedFixedMargin: z.number().min(0).optional(),
    oneWay: z.boolean().optional(),
    originIataCode: z.string().optional(),
    originIataCodes: z.array(z.string()).optional(),
    outboundPeriods: z.array(marginPeriodSchema).optional(),
    returnPeriods: z.array(marginPeriodSchema).optional(),
    roundTrip: z.boolean().optional(),
    routeSegments: z.array(routeSegmentSchema).optional(),
    space: z.string().optional(),
};
/**
 * Conditional required fields keyed off `type` (legacy `@ValidateIf(o => o.type === ...)`
 * on fields with no `@IsOptional`), modeled with superRefine since the downstream
 * margin service reads everything off a flat dto.
 */
export const createMarginSchema = z
    .object({
    ...marginCommonFields,
    name: z.string(),
    companyCodes: z.array(z.string()).optional(),
    priceRanges: z.array(marginPriceRangeSchema).optional(),
    type: z.nativeEnum(MarginType),
})
    .superRefine((data, ctx) => {
    const require = (field, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [field] });
    if (data.type === MarginType.COMPANY) {
        if (!data.companyCodes || data.companyCodes.length < 1) {
            require('companyCodes', 'At least one company code is required for COMPANY margins');
        }
        if (data.companyFixedMargin === undefined) {
            require('companyFixedMargin', 'companyFixedMargin is required for COMPANY margins');
        }
    }
    if (data.type === MarginType.NEGOTIATED_RATE && data.negotiatedFixedMargin === undefined) {
        require('negotiatedFixedMargin', 'negotiatedFixedMargin is required for NEGOTIATED_RATE margins');
    }
    if (data.type === MarginType.DESTINATION_FROM_TO_FRANCE &&
        data.fixedMarginAmount === undefined) {
        require('fixedMarginAmount', 'fixedMarginAmount is required for DESTINATION_FROM_TO_FRANCE margins');
    }
    if (data.type === MarginType.DESTINATION_OTHER_TO_OTHER &&
        (!data.priceRanges || data.priceRanges.length < 1)) {
        require('priceRanges', 'At least one price range is required for DESTINATION_OTHER_TO_OTHER margins');
    }
});
export const updateMarginSchema = z.object({
    ...marginCommonFields,
    name: z.string().optional(),
    companyCodes: z.array(z.string()).optional(),
    priceRanges: z.array(marginPriceRangeSchema).min(1).optional(),
    type: z.nativeEnum(MarginType).optional(),
});
